import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Heart,
  Send,
  Calendar,
  FileText,
  CheckCircle2,
  XCircle,
  User,
  MessageSquare,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  Activity,
  Wallet,
  Share2,
  Lock,
  Clock,
  FileCheck,
  Receipt,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { ReviewerPanel } from './GovernanceViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Style helpers for data request status
const getRequestCardStyle = (isGranted, isWithheld) => {
  if (isGranted) return 'bg-emerald-500/5 border-emerald-500/20';
  if (isWithheld) return 'bg-rose-500/5 border-rose-500/20';
  return 'bg-amber-500/5 border-amber-500/20';
};

const getRequestStatusLabel = (isGranted, isWithheld) => {
  if (isGranted) return 'Shared';
  if (isWithheld) return 'Withheld';
  return 'Pending';
};

// Provenance badge component
const ProvenanceBadge = ({ level }) => {
  const config = {
    patient_provided: { label: 'Patient Provided', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    clinician_issued: { label: 'Clinician Issued', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    diagnostic_source: { label: 'Diagnostic Source', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    specialist_reviewed: { label: 'Specialist Reviewed', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
  };
  
  const { label, color } = config[level] || { label: level, color: 'bg-slate-500/20 text-slate-400' };
  
  return (
    <Badge variant="outline" className={`text-xs ${color}`}>
      {label}
    </Badge>
  );
};

// Sharing status badge
const SharingBadge = ({ status }) => {
  const config = {
    shared: { label: 'Shared', icon: Share2, color: 'bg-emerald-500/20 text-emerald-400' },
    withheld: { label: 'Private', icon: Lock, color: 'bg-slate-500/20 text-slate-400' },
    pending: { label: 'Pending', icon: Clock, color: 'bg-amber-500/20 text-amber-400' }
  };
  
  const { label, icon: Icon, color } = config[status] || config.withheld;
  
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </div>
  );
};

// Wallet credential card
const WalletCredentialCard = ({ credential }) => (
  <div className={`p-4 rounded-lg border ${
    credential.sharing_status === 'shared' 
      ? 'bg-emerald-500/5 border-emerald-500/20' 
      : 'bg-slate-800/50 border-slate-700'
  }`}>
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-slate-400" />
        <span className="font-medium text-white">{credential.title}</span>
      </div>
      <SharingBadge status={credential.sharing_status} />
    </div>
    
    <div className="text-sm text-slate-400 mb-3">{credential.summary}</div>
    
    <div className="flex items-center justify-between">
      <ProvenanceBadge level={credential.provenance_level} />
      <div className="text-xs text-slate-500">
        Issued by {credential.issued_by}
      </div>
    </div>
    
    {credential.shared_with?.length > 0 && (
      <div className="mt-3 pt-3 border-t border-slate-700/50">
        <div className="flex items-center gap-1 text-xs text-emerald-400">
          <Eye className="w-3 h-3" />
          Shared with: {credential.shared_with.join(', ')}
        </div>
      </div>
    )}
  </div>
);

// Data request card
const DataRequestCard = ({ request }) => {
  const isGranted = request.status === 'shared';
  const isWithheld = request.status === 'withheld';
  
  return (
    <div className={`p-4 rounded-lg border ${getRequestCardStyle(isGranted, isWithheld)}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {isGranted ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          ) : isWithheld ? (
            <XCircle className="w-4 h-4 text-rose-400" />
          ) : (
            <AlertCircle className="w-4 h-4 text-amber-400" />
          )}
          <span className="font-medium text-white">
            {getRequestStatusLabel(isGranted, isWithheld)}
          </span>
        </div>
        <Badge variant="outline" className={`text-xs ${
          request.urgency === 'Routine' 
            ? 'border-slate-500/50 text-slate-400' 
            : 'border-amber-500/50 text-amber-400'
        }`}>
          {request.urgency}
        </Badge>
      </div>
      
      <div className="text-sm text-slate-400 mb-2">
        <span className="text-slate-500">From:</span> {request.requested_by}
      </div>
      
      <div className="text-sm text-slate-400 mb-2">
        <span className="text-slate-500">Requested:</span> {request.data_requested?.join(', ')}
      </div>
      
      <div className="text-sm text-slate-400 mb-3">
        <span className="text-slate-500">Purpose:</span> {request.purpose}
      </div>
      
      {request.patient_response && (
        <div className={`text-xs p-2 rounded ${
          isGranted ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
        }`}>
          {request.patient_response}
        </div>
      )}
    </div>
  );
};

// Receipt pack entry
const ReceiptPackEntry = ({ entry, index }) => (
  <div className="flex gap-3 pb-4 border-b border-slate-700/50 last:border-0 last:pb-0">
    <div className="flex flex-col items-center">
      <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400 text-sm font-medium">
        {index + 1}
      </div>
      {index < 5 && <div className="w-px h-full bg-slate-700 mt-2" />}
    </div>
    <div className="flex-1 pt-1">
      <div className="font-medium text-white mb-1">{entry.action}</div>
      <div className="text-sm text-slate-400 mb-2">
        {entry.actor} • {entry.outcome}
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {entry.data_involved?.map((item, i) => (
          <Badge key={`${entry.receipt_hash}-data-${i}`} variant="outline" className="text-xs border-slate-600 text-slate-400">
            {item}
          </Badge>
        ))}
      </div>
      <div className="text-xs text-slate-600 font-mono">
        {entry.receipt_hash}
      </div>
    </div>
  </div>
);

// Message bubble
const MessageBubble = ({ message }) => {
  const isUser = message.is_user;
  const isSystem = message.is_system;

  const getMessageStyle = () => {
    if (isUser) return 'bg-rose-600 text-white rounded-br-sm';
    if (isSystem) return 'bg-slate-700 text-slate-300 rounded-bl-sm';
    return 'bg-slate-800 text-white rounded-bl-sm';
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div className={`rounded-2xl px-4 py-3 ${getMessageStyle()}`}>
          {!isUser && (
            <div className="text-xs text-slate-400 mb-1">{message.sender}</div>
          )}
          <div className="text-sm">{message.content}</div>
        </div>
        {message.governance_note && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <Shield className="w-3 h-3" />
            {message.governance_note}
          </div>
        )}
      </div>
    </div>
  );
};

// Appointment card
const AppointmentCard = ({ appointment }) => (
  <div className={`p-3 rounded-lg border ${
    appointment.status === 'Confirmed' 
      ? 'bg-emerald-500/10 border-emerald-500/30' 
      : 'bg-amber-500/10 border-amber-500/30'
  }`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-white">{appointment.appointment_type}</span>
      <Badge className={appointment.status === 'Confirmed' ? 'bg-emerald-500' : 'bg-amber-500'}>
        {appointment.status}
      </Badge>
    </div>
    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
      <Calendar className="w-3 h-3" />
      {new Date(appointment.scheduled_for).toLocaleDateString()} at {new Date(appointment.scheduled_for).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
    </div>
    <div className="text-xs text-slate-500">{appointment.location}</div>
  </div>
);

// Receipt Pack Modal
const ReceiptPackModal = ({ isOpen, onClose, receiptPack }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-rose-400" />
            <span className="font-semibold text-white">Receipt Pack</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </Button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="mb-4 p-3 bg-slate-800/50 rounded-lg">
            <div className="text-sm text-slate-400 mb-2">This receipt pack documents each governance action in your care pathway:</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-center p-2 bg-emerald-500/10 rounded">
                <div className="text-emerald-400 font-medium">{receiptPack?.filter(r => r.action.includes('Shared')).length || 0}</div>
                <div className="text-slate-500">Items Shared</div>
              </div>
              <div className="text-center p-2 bg-rose-500/10 rounded">
                <div className="text-rose-400 font-medium">{receiptPack?.filter(r => r.action.includes('Withheld')).length || 0}</div>
                <div className="text-slate-500">Items Withheld</div>
              </div>
              <div className="text-center p-2 bg-slate-700 rounded">
                <div className="text-white font-medium">{receiptPack?.length || 0}</div>
                <div className="text-slate-500">Total Actions</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            {receiptPack?.map((entry, i) => (
              <ReceiptPackEntry key={entry.receipt_hash} entry={entry} index={i} />
            ))}
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700 bg-slate-800/30">
          <div className="text-xs text-slate-500 text-center">
            All actions recorded with cryptographic receipts. You may request a full audit trail at any time.
          </div>
        </div>
      </div>
    </div>
  );
};

// Pathway progress
const PathwayProgress = ({ pathway }) => {
  const totalSteps = pathway?.expected_milestones?.length || 0;
  const completedSteps = pathway?.completed_milestones?.length || 0;
  const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-400">Progress</span>
        <span className="text-white">{completedSteps} of {totalSteps} steps</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-rose-500 to-rose-400 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="space-y-2">
        {pathway?.expected_milestones?.map((milestone, i) => {
          const isComplete = pathway?.completed_milestones?.includes(milestone);
          return (
            <div key={`milestone-${i}`} className="flex items-center gap-2 text-sm">
              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                isComplete ? 'bg-emerald-500' : 'bg-slate-700'
              }`}>
                {isComplete && <CheckCircle2 className="w-3 h-3 text-white" />}
              </div>
              <span className={isComplete ? 'text-white' : 'text-slate-500'}>{milestone}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AssistedPatientDemo = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [showReviewer, setShowReviewer] = useState(false);
  const [showReceiptPack, setShowReceiptPack] = useState(false);
  const [activeTab, setActiveTab] = useState('wallet');

  useEffect(() => {
    const loadDemo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/governance/patient/demo`);
        if (res.ok) {
          const json = await res.json();
          setData(json);
        } else {
          setLoadError('Failed to load patient demo');
        }
      } catch (err) {
        setLoadError('Failed to load patient demo');
      } finally {
        setLoading(false);
      }
    };
    loadDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - load once on mount, API_URL is a module constant

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center text-red-400">{loadError}</div>
      </div>
    );
  }

  const { workflow, pathway, wallet_credentials, pending_requests, receipt_pack, appointments, messages } = data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/demos" className="text-slate-400 hover:text-white" data-testid="back-to-demos">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-400" />
              <span className="font-semibold text-white">Assisted Patient Advisor</span>
            </div>
            <Badge variant="outline" className="border-rose-500/50 text-rose-400">
              Wallet-Centric
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReceiptPack(true)}
              className="border-slate-600 text-slate-400 hover:text-white"
              data-testid="view-receipt-pack-btn"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Receipt Pack
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReviewer(!showReviewer)}
              className={`border-slate-600 ${showReviewer ? 'bg-rose-600 text-white border-rose-600' : 'text-slate-400'}`}
              data-testid="toggle-governance-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              {showReviewer ? 'Hide Governance' : 'Show Governance'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className={`grid gap-6 ${showReviewer ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
          
          {/* Main Panel */}
          <div className={showReviewer ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <div className="space-y-4">
              
              {/* Status Banner */}
              <Card className="bg-rose-500/10 border-rose-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Activity className="w-6 h-6 text-rose-400" />
                    <div>
                      <div className="text-white font-medium">{workflow?.status}</div>
                      <div className="text-sm text-rose-400">{workflow?.next_expected_action}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tab Navigation */}
              <div className="flex gap-2 p-1 bg-slate-800/50 rounded-lg">
                {[
                  { id: 'wallet', label: 'My Wallet', icon: Wallet },
                  { id: 'requests', label: 'Data Requests', icon: Share2 },
                  { id: 'messages', label: 'Messages', icon: MessageSquare }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-rose-600 text-white' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Wallet Tab */}
              {activeTab === 'wallet' && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-rose-400" />
                      Your Health Wallet
                      <Badge variant="outline" className="ml-auto border-slate-600 text-slate-400">
                        {wallet_credentials?.length || 0} credentials
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-400 mb-4">
                      Your wallet holds your health credentials. You control what gets shared.
                    </div>
                    <div className="space-y-3" data-testid="wallet-credentials">
                      {wallet_credentials?.map(cred => (
                        <WalletCredentialCard key={cred.credential_id} credential={cred} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Data Requests Tab */}
              {activeTab === 'requests' && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-rose-400" />
                      Data Sharing Requests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-400 mb-4">
                      Clinicians may request access to your wallet credentials. You decide what to share.
                    </div>
                    <div className="space-y-3" data-testid="data-requests">
                      {pending_requests?.map(req => (
                        <DataRequestCard key={req.request_id} request={req} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-rose-400" />
                      Messages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-4 max-h-80 overflow-y-auto" data-testid="messages-list">
                      {messages?.map(msg => (
                        <MessageBubble key={msg.message_id} message={msg} />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-rose-500"
                        data-testid="message-input"
                      />
                      <Button className="bg-rose-600 hover:bg-rose-700" data-testid="send-message-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* What's Been Shared Summary */}
              <Card className="bg-slate-800/30 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-700 rounded-lg">
                      <Eye className="w-4 h-4 text-slate-400" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white mb-1">What's been shared</div>
                      <div className="text-sm text-slate-400">{workflow?.shared_with_summary}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Side Panel */}
          {showReviewer ? (
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 sticky top-20">
                <CardContent className="p-4">
                  <ReviewerPanel workflow={workflow} title="Governance: Patient Pathway" />
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Pathway Progress */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Activity className="w-4 h-4 text-rose-400" />
                    Care Pathway
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-3">
                    <div className="text-lg font-semibold text-white">{pathway?.pathway_type}</div>
                    <div className="text-sm text-slate-400">{pathway?.primary_condition}</div>
                  </div>
                  <PathwayProgress pathway={pathway} />
                </CardContent>
              </Card>

              {/* Appointments */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-rose-400" />
                    Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="appointments-list">
                    {appointments?.map(apt => (
                      <AppointmentCard key={apt.appointment_id} appointment={apt} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-between border-slate-600 text-slate-300 hover:text-white"
                      onClick={() => setShowReceiptPack(true)}
                      data-testid="quick-receipt-pack-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Receipt className="w-4 h-4" />
                        View Receipt Pack
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between border-slate-600 text-slate-300 hover:text-white"
                      onClick={() => setShowReviewer(true)}
                      data-testid="quick-governance-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        View Governance Trail
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Pack Modal */}
      <ReceiptPackModal 
        isOpen={showReceiptPack} 
        onClose={() => setShowReceiptPack(false)} 
        receiptPack={receipt_pack}
      />
    </div>
  );
};

export default AssistedPatientDemo;
